/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.marquee = this.querySelector('.announcement__marquee');
      this.marqueeWrapper = this.querySelector('.announcement__marquee-wrapper');
      this.marqueeContent = this.querySelector('.announcement__marquee-content');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;
      this.isMarqueeMobile = this.dataset.marqueeMobile === 'true';
      this.isMarqueeDesktop = this.dataset.marqueeDesktop === 'true';
      this.marqueeSpeed = parseInt(this.dataset.marqueeSpeed) || 10;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.animationId = null;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }

      if (this.marquee || this.marqueeWrapper) {
        const marqueeElement = this.marqueeWrapper || this.marquee;
        marqueeElement.addEventListener('mouseenter', this.pauseMarquee.bind(this));
        marqueeElement.addEventListener('mouseleave', this.resumeMarquee.bind(this));
        marqueeElement.addEventListener('focusin', this.pauseMarquee.bind(this));
        marqueeElement.addEventListener('focusout', this.resumeMarquee.bind(this));
      }

      // Listen for reduced motion preference changes
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      motionQuery.addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches;
        if ((this.isMarqueeMobile || this.isMarqueeDesktop) && this.marquee) {
          this.initMarquee();
        }
      });

      // Listen for window resize to recalculate marquee and handle desktop/mobile switching
      if ((this.isMarqueeMobile || this.isMarqueeDesktop) && (this.marquee || this.marqueeWrapper)) {
        const marqueeElement = this.marqueeWrapper || this.marquee;
        this.resizeObserver = new ResizeObserver(debounce(() => {
          this.initMarquee();
        }, 250));
        this.resizeObserver.observe(marqueeElement);
        
        // Listen for window resize to handle desktop/mobile switching
        this.handleResize = debounce(() => {
          this.initMarquee();
        }, 250);
        window.addEventListener('resize', this.handleResize);
        
        // Also listen for font load events that might affect text width
        document.fonts.ready.then(() => {
          this.initMarquee();
        });
      }
    }

    /**
     * Start sliders a slidin' or marquee a scrollin'
     */
    init() {
      if ((this.isMarqueeMobile || this.isMarqueeDesktop) && (this.marquee || this.marqueeWrapper)) {
        this.initMarquee();
      } else if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Initialize marquee animation - with mobile/desktop control
     */
    initMarquee() {
      // Ensure theme object is available before proceeding
      if (!window.theme || !window.theme.elementUtil) {
        // Retry after a short delay if theme isn't ready
        setTimeout(() => this.initMarquee(), 100);
        return;
      }

      // Support both new and legacy structure
      const marqueeElement = this.marqueeWrapper || this.marquee;
      const marqueeContent = this.marqueeContent || (this.marquee ? this.marquee.querySelector('.announcement__text') : null);
      
      if (!marqueeElement || !marqueeContent) return;

      // Check if marquee should be enabled based on device type
      const isMobile = window.innerWidth < 769;
      const shouldEnableMarquee = isMobile ? this.isMarqueeMobile : this.isMarqueeDesktop;
      
      // If marquee is disabled for this device type, reset to static display
      if (!shouldEnableMarquee) {
        marqueeContent.style.animation = 'none';
        marqueeContent.style.transform = 'none';
        marqueeContent.style.position = 'static';
        marqueeElement.setAttribute('aria-live', 'polite');
        return;
      }

      // Reset animation and styles
      marqueeContent.style.animation = 'none';
      marqueeContent.style.transform = 'translate3d(0, 0, 0)';
      marqueeContent.offsetHeight; // Trigger reflow

      // Respect reduced motion preference
      if (this.prefersReducedMotion) {
        marqueeContent.style.transform = 'none';
        marqueeContent.style.position = 'static';
        marqueeContent.style.animation = 'none';
        marqueeElement.setAttribute('aria-live', 'polite');
        return;
      }

      // Set up intersection observer for performance
      if (!this.intersectionObserver) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            const content = this.marqueeContent || (this.marquee ? this.marquee.querySelector('.announcement__text') : null);
            if (content) {
              if (entry.isIntersecting && !this.isPaused) {
                content.style.animationPlayState = 'running';
              } else {
                content.style.animationPlayState = 'paused';
              }
            }
          });
        }, { threshold: 0.1 });
        this.intersectionObserver.observe(marqueeElement);
      }

      // Wait for next frame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Create seamless loop by duplicating content
        this.createSeamlessLoop(marqueeElement, marqueeContent);
        
        // Wait for another frame to ensure cloned content is rendered
        requestAnimationFrame(() => {
          // Calculate animation duration based on quarter of the content width for smooth circular loop
          const fullContentWidth = marqueeContent.scrollWidth;
          const quarterContentWidth = fullContentWidth / 4; // Since we have 4 copies (1 original + 3 clones)
          
          // Use the configured speed for both mobile and desktop
          const adjustedSpeed = this.marqueeSpeed;
          
          // Calculate duration for smooth animation - minimum 5 seconds for readability
          const duration = Math.max(quarterContentWidth / adjustedSpeed, 5);
            
          // Set animation properties with smooth circular keyframes
          const animationName = document.dir === 'rtl' ? 'marquee-smooth-rtl' : 'marquee-smooth';
          marqueeContent.style.animation = `${animationName} ${duration}s linear infinite`;
          
          // Ensure smooth performance
          marqueeContent.style.animationFillMode = 'none';
          marqueeContent.style.animationTimingFunction = 'linear';
          
          // Add accessibility attributes
          marqueeElement.setAttribute('aria-live', 'off');
          marqueeElement.setAttribute('role', 'marquee');
          marqueeContent.setAttribute('aria-label', `Scrolling announcement: ${marqueeContent.textContent}`);
        });
      });
    }

    /**
     * Create seamless loop by duplicating content
     */
    createSeamlessLoop(marqueeElement, marqueeContent) {
      // Remove any existing duplicated content
      const existingClones = marqueeContent.querySelectorAll('.marquee-clone');
      existingClones.forEach(clone => clone.remove());

      // Get all original content items
      const originalItems = Array.from(marqueeContent.children).filter(child => !child.classList.contains('marquee-clone'));
      
      // Clone and append the content multiple times to create seamless loop
      // We create multiple copies to ensure smooth circular animation
      for (let i = 0; i < 3; i++) {
        originalItems.forEach(item => {
          const clone = item.cloneNode(true);
          clone.classList.add('marquee-clone');
          clone.setAttribute('aria-hidden', 'true'); // Hide from screen readers
          marqueeContent.appendChild(clone);
        });
      }
    }


    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      if (!theme.mediaMatches.md && !menuAnnouncementLinks) {
        // Move localization to mobile
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const mobNavUl = document.createElement('ul');
        mobNav.classList.add('secondary-nav');

        this.links.forEach((link) => {
          mobNav.innerHTML += `<li><a class="secondary-nav__item" href="${link.href}">${link.innerText}</a>`;
        });

        mobNav.appendChild(mobNavUl);
        this.menu.appendChild(mobNav);
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      // Don't re-query elements that are already set in constructor - this can break marquee functionality
      if (!this.localization) {
        this.localization = this.querySelector('.announcement__localization');
      }
      if (!this.links || this.links.length === 0) {
        this.links = this.querySelectorAll('.js-announcement-link');
      }
      if (!this.menu) {
        this.menu = document.querySelector('.main-menu__content');
      }
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization && this.localization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization && this.localization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }

    /**
     * Pauses the marquee animation
     */
    pauseMarquee() {
      if (this.prefersReducedMotion) return;
      
      this.isPaused = true;
      const marqueeElement = this.marqueeWrapper || this.marquee;
      const marqueeContent = this.marqueeContent || (this.marquee ? this.marquee.querySelector('.announcement__text') : null);
      
      if (!marqueeElement || !marqueeContent) return;
      
      if (marqueeContent && marqueeContent.style.animation !== 'none') {
        marqueeContent.style.animationPlayState = 'paused';
        marqueeElement.setAttribute('aria-live', 'polite');
      }
    }

    /**
     * Resumes the marquee animation
     */
    resumeMarquee() {
      if (this.prefersReducedMotion) return;
      
      this.isPaused = false;
      const marqueeElement = this.marqueeWrapper || this.marquee;
      const marqueeContent = this.marqueeContent || (this.marquee ? this.marquee.querySelector('.announcement__text') : null);
      
      if (!marqueeElement || !marqueeContent || !window.theme || !window.theme.elementUtil) return;
      
      if (marqueeContent && marqueeContent.style.animation !== 'none' && theme.elementUtil.isInViewport(marqueeElement)) {
        marqueeContent.style.animationPlayState = 'running';
        marqueeElement.setAttribute('aria-live', 'off');
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
